/**
 * Capture a subtree as if the app were in light mode — VH-133
 *
 * html2canvas-pro rasterises the *live* DOM, colours and all. In dark mode
 * that produces a black page with light text: unreadable when printed,
 * ink-hungry, and plainly wrong for a health report a user forwards to their
 * doctor. So exports are always light, whatever theme is on screen.
 *
 * The mechanism is one attribute. `[data-force-light]` in styles/index.css
 * re-declares the light token values for the subtree it is set on, so
 * everything inside resolves to light colours while the rest of the app stays
 * in the user's chosen theme. The attribute is removed again as soon as the
 * canvas exists — the user never sees the element flicker, because the capture
 * is synchronous from the DOM's point of view.
 *
 * `finally` matters more than it looks: if the capture throws, an element left
 * permanently pinned to light would be a visible white block sitting in a dark
 * page, and the user would have no way to clear it short of a reload.
 */
export const withLightTheme = async (element, capture) => {
  if (!element) return capture()

  const alreadyForced = element.hasAttribute('data-force-light')
  if (!alreadyForced) element.setAttribute('data-force-light', '')

  try {
    return await capture()
  } finally {
    if (!alreadyForced) element.removeAttribute('data-force-light')
  }
}

export default withLightTheme
